import {
  Stack,
  Typography,
  OutlinedInput,
  FormControl,
  FormHelperText,
} from '@mui/material';
import React, { ChangeEvent, FC, useCallback, useState, useRef } from 'react';
import Icon from '@/components/ui/Icon';
import { TextInputProps } from '@/components/ui/TextInput';

import style from './style';

const parseGermanNumber = (value: string): number => {
  if (value.includes(',')) {
    return parseFloat(value.replace(/\./g, '').replace(',', '.'));
  }
  return parseFloat(value);
};

const formatGermanNumber = (
  value: string | number,
  fractionDigits = 2,
): string => {
  const num =
    typeof value === 'string' ? parseGermanNumber(value) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('de-DE', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
};

export interface TextInputInlineProps extends TextInputProps {
  subtitle?: string;
  labelFixedWidth?: string;
  inForm?: boolean;
  decimalPlaces?: number;
}

const TextInputInline: FC<TextInputInlineProps> = ({
  label,
  subtitle,
  placeholder,
  onTextChange,
  type,
  value,
  error,
  touched,
  icon,
  hideErrorMessage,
  labelFixedWidth,
  fullWidth,
  inForm,
  decimalPlaces = 2,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(() => {
    if (type === 'number' && value) {
      return formatGermanNumber(value, decimalPlaces);
    }
    return value || '';
  });
  const valueRef = useRef(value);
  const prevValueRef = useRef(value);

  // Formik-Wert von außen synchronisieren (wenn sich value ändert und Feld nicht fokussiert ist)
  if (value !== prevValueRef.current && !isFocused) {
    prevValueRef.current = value;
    valueRef.current = value;
    if (type === 'number' && value) {
      setLocalValue(formatGermanNumber(value, decimalPlaces));
    } else {
      setLocalValue(value || '');
    }
  }
  valueRef.current = value;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (type === 'number') {
        if (newValue === '') {
          setLocalValue('');
          onTextChange?.('');
          return;
        }
        // Erlaube deutsches Zahlenformat: Ziffern, Punkte und Komma
        const germanNumberPattern = /^[\d.,]*$/;
        if (!germanNumberPattern.test(newValue)) {
          return;
        }
        // Lokalen Wert setzen (erlaubt Komma-Eingabe ohne Formik-Validierungsfehler)
        setLocalValue(newValue);
        // Wenn der Wert ein gültiges deutsches Format ist, sofort an Formik übergeben
        const normalized = newValue.replace(/\./g, '').replace(',', '.');
        const num = parseFloat(normalized);
        if (!isNaN(num)) {
          onTextChange?.(String(num));
        }
        return;
      }

      setLocalValue(newValue);
      onTextChange?.(newValue);
    },
    [onTextChange, type],
  );

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    if (type === 'number') {
      const current = valueRef.current;
      setLocalValue(current ? formatGermanNumber(current, decimalPlaces) : '');
    }
  }, [type, decimalPlaces]);

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (type === 'number') {
      const currentValue = e.target.value;
      if (currentValue) {
        const normalized = currentValue.replace(/\./g, '').replace(',', '.');
        const num = parseFloat(normalized);
        if (!isNaN(num)) {
          onTextChange?.(String(num));
          setLocalValue(formatGermanNumber(num, decimalPlaces));
        }
      } else {
        setLocalValue('');
      }
    }
    setIsFocused(false);
    props.onBlur?.(e);
  };

  return (
    <FormControl style={style.container} error={!!error}>
      <Stack flexDirection="row" alignItems="center">
        <Stack width={labelFixedWidth ? labelFixedWidth : '200px'} gap={1}>
          {!!label && (
            <Typography
              variant="label"
              sx={style.label}
              color={error ? 'error.main' : 'text.primary'}
            >
              {label}
            </Typography>
          )}
          {!!subtitle && (
            <Typography
              variant="klein"
              color={error ? 'error.main' : 'text.primary'}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
        <Stack flex={1}>
          <OutlinedInput
            {...props}
            value={localValue}
            placeholder={placeholder}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            size="small"
            sx={[style.input, !!inForm && style.inputInForm]}
            aria-describedby="field-text-input-descriptor"
            startAdornment={
              icon && <Icon sx={style.icon} icon={icon} size={24} />
            }
          />
          {!hideErrorMessage && (
            <FormHelperText sx={style.error} id="field-text-input-descriptor">
              {error}
            </FormHelperText>
          )}
        </Stack>
      </Stack>
    </FormControl>
  );
};

export default TextInputInline;
